import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
} from '@mui/icons-material';
import DocumentService from 'services/document';
import {
  FileData, FileType, UpdateFileDataInput,
} from 'API';
import {
  ALLOWED_FILE_TYPES, DICTIONARY, MAX_FILE_SIZE,
} from 'shared/constants';
import UserContext from 'shared/contexts/UserContext';
import { updateFileData } from 'graphql/customFileQueries';
import useUploadProgress from 'shared/hooks/useUploadProgress';
import { onUpdateFileData } from 'graphql/subscriptions';
import useCreateFileUploadActivity from 'shared/hooks/useCreateFileUploadActivity';
import FileExpirationDate from 'components/FileExpirationDate';
import FileNameText from 'components/FileName';
import AlertContext from 'shared/contexts/AlertContext';
import IconRow from './IconRow';
import IconRowCell from './IconRowCell';
import InlineFileModalButton from './InlineFileModalButton';
import FileTypeSelect from './forms/FileTypeSelect';
import { client } from 'lib/graphql-client';

interface Props {
  allowedTypes?: string;
  fileType?: FileType;
  fileTypeOptions?: Array<FileType>;
  label?: string;
  onSave?: (file: FileData) => void;
  onCancel?: (file: FileData) => void;
  onIsLoadingChange?: (loading: boolean) => void;
  existingFile?: FileData;
  ownership?: DocumentOwnership; // Who has rights to document
  storage?: DocumentStorage; // Where file should be stored
  storageMap?: StorageMap; // Aa way to derive storage based on fileType
}

const FileUpload: React.FC<Props> = ({
  allowedTypes,
  existingFile,
  fileType,
  fileTypeOptions,
  label,
  onSave,
  onCancel,
  onIsLoadingChange,
  ownership,
  storage,
  storageMap,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputFile = inputRef?.current?.files
    ? inputRef.current.files[0]
    : null;
  const [isLoading, setIsLoading] = useState(false);
  const [
    uploadedFile, setUploadedFile,
  ] = useState<FileData | undefined>(existingFile);
  const [type, setType] = useState<FileType | undefined>(
    fileType || uploadedFile?.fileType || undefined,
  );
  const [, setAlert] = useContext(AlertContext);
  const [, setHasUploadedFile] = useState<boolean>(false);
  const [progress, setProgress, progressCallback] = useUploadProgress();
  const [userState] = useContext(UserContext);
  const { person } = userState;
  const createActivity = useCreateFileUploadActivity();

  useEffect(() => {
    let subscription: any;
    if (uploadedFile?.id) {
      subscription = (client.graphql({
        query: onUpdateFileData,
        variables: {
          id: uploadedFile?.id,
        },
      }) as any).subscribe(
        {
          next: (data: any) => {
            if (data?.data?.onUpdateFileData) {
              setUploadedFile(data.data.onUpdateFileData);
              setType(data.data.onUpdateFileData.fileType);
            }
          },
          // eslint-disable-next-line no-console
          error: (error: any) => console.warn(error),
        },
      );
    }

    return () => {
      if (uploadedFile?.id) {
        subscription.unsubscribe();
      }
    };
  }, [uploadedFile?.id]);

  useEffect(() => {
    if (onIsLoadingChange) {
      onIsLoadingChange(isLoading);
    }
  }, [isLoading]);

  const handleUploadFile = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleRemoveFile = () => {
    setHasUploadedFile(false);
    setUploadedFile(undefined);
    if (inputRef && inputRef.current) {
      inputRef.current.value = '';
    }

    if (uploadedFile) {
      if (onCancel) onCancel(uploadedFile);
      DocumentService.deleteFileData(uploadedFile.id as string);
    }
  };

  const handleFileTypeChange = (
    e: SelectChangeEvent<any>,
  ) => {
    const originalType = type;
    setType(e.target.value);

    if (uploadedFile) {
      const input: UpdateFileDataInput = {
        id: uploadedFile.id!,
        fileType: e.target.value,
      };

      let thisStorage;

      if (storage) {
        thisStorage = storage;
      } else if (storageMap) {
        thisStorage = storageMap(e.target.value);
      }

      if (thisStorage?.companyID) {
        input.companyID = thisStorage.companyID;
      }
      if (thisStorage?.ownerNationalID) {
        input.ownerNationalID = thisStorage.ownerNationalID;
      }
      if (thisStorage?.processRequestID) {
        input.processRequestID = thisStorage.processRequestID;
      }

      try {
        client.graphql({
          query: updateFileData,
          variables: { input },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        setAlert(['Something went wrong changing the file type.', 'error']);
        setType(originalType);
      }
    }
  };

  useEffect(() => {
    const upload = async () => {
      try {
        setIsLoading(true);

        let thisStorage;

        if (storage) {
          thisStorage = storage;
        } if (storageMap) {
          thisStorage = storageMap(type);
        }

        const newFile = await DocumentService.putDocument(
          inputFile!,
          person.id,
          {
            fileName: inputFile!.name,
            companyID: ownership?.companyID,
            foreignNationalID: ownership?.foreignNationalID,
            fileType: type,
          },
          thisStorage,
          undefined,
          progressCallback,
        );

        setUploadedFile(newFile);
        await createActivity(newFile);

        setProgress(0);
        setIsLoading(false);

        if (onSave) {
          onSave(newFile);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        setIsLoading(false);
        setAlert([DICTIONARY.UploadError, 'error']);
      }
    };

    if (inputFile) {
      if (inputFile.size > MAX_FILE_SIZE.bytes) {
        setAlert([`The maximum file size is ${MAX_FILE_SIZE.text}.`, 'error']);
        if (inputRef && inputRef.current) {
          inputRef.current.value = '';
          setHasUploadedFile(false);
        }
      } else {
        upload();
      }
    }
  }, [inputFile]);

  const CancelCloud = () => (
    <IconButton
      onClick={handleRemoveFile}
      sx={{ '&:hover': { backgroundColor: 'inherit' } }}
      size="large"
    >
      <div>
        <CloseIcon color="primary" fontSize="large" />
        <Typography color="primary" variant="button" sx={{ display: 'block', lineHeight: 'normal', textDecoration: 'underline' }}>
          DELETE
        </Typography>
      </div>
    </IconButton>
  );

  const UploadCloud = () => (
    <IconButton
      onClick={handleUploadFile}
      sx={{ '&:hover': { backgroundColor: 'inherit' } }}
      size="large"
    >
      <div>
        <CloudUploadIcon color="primary" fontSize="large" />
        <Typography color="primary" variant="button" sx={{ display: 'block', lineHeight: 'normal', textDecoration: 'underline' }}>
          UPLOAD
        </Typography>
      </div>
    </IconButton>
  );

  const FileName = () => (
    <Box
      flexGrow={1}
      display="flex"
      justifyContent="center"
      flexDirection="column"
    >
      {label && <Typography>{label}</Typography>}
      <Typography
        variant="caption"
        sx={(t) => ({ color: t.palette.tertiary6, fontStyle: 'italic' })}
      >
        {(uploadedFile)
          ? (
            <>
              <InlineFileModalButton
                file={uploadedFile}
                fileTypeOptions={fileTypeOptions}
                withSidebar
              >
                <FileNameText file={uploadedFile} withUploadDate />
              </InlineFileModalButton>
              <FileExpirationDate file={uploadedFile} />
            </>
          )
          : 'No file has been uploaded'}
      </Typography>
    </Box>
  );

  const Input = () => (
    <>
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '5rem' }}>
          <CircularProgress
            sx={{ height: '4rem', width: '4rem' }}
            variant="determinate"
            value={progress}
          />
        </Box>
      )}
      { !isLoading && uploadedFile && <CancelCloud /> }
      { !isLoading && !uploadedFile && <UploadCloud /> }
      <input
        type="file"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={() => {
          setHasUploadedFile(true);
        }}
        accept={allowedTypes || ALLOWED_FILE_TYPES}
      />
    </>
  );

  if (fileTypeOptions && fileTypeOptions.length > 0) {
    return (
      <Box
        sx={(t) => ({
          alignItems: 'center',
          display: 'grid',
          gridTemplateColumns: 'minMax(10rem, 18rem) 1fr 5.5rem',
          columnGap: '1rem',
          [t.breakpoints.down('lg')]: {
            gridTemplateColumns: '1fr auto',
            gridTemplateRows: 'auto auto',
          },
        })}
      >
        <Box sx={(t) => ({ [t.breakpoints.down('lg')]: { gridColumn: '1 / 3' } })}>
          <FileTypeSelect
            sx={{ width: '100%' }}
            fileTypeOptions={fileTypeOptions}
            onChange={handleFileTypeChange}
            value={type}
          />
        </Box>
        <Box sx={(t) => ({ [t.breakpoints.down('lg')]: { gridColumn: '1 / 2', gridRow: '2 / 3' } })}>
          <FileName />
          <FileExpirationDate file={uploadedFile} />
        </Box>
        <Box sx={(t) => ({ display: 'flex', justifyContent: 'center', [t.breakpoints.down('lg')]: { gridColumn: '2 / 3', gridRow: '2 / 3' } })}>
          <Input />
        </Box>
      </Box>
    );
  }

  return (
    <IconRow>
      <IconRowCell type="icon" style={{ alignSelf: 'center' }}>
        <InsertDriveFileOutlinedIcon fontSize="large" />
      </IconRowCell>
      <IconRowCell type="text">
        <FileName />
        <FileExpirationDate file={uploadedFile} />
      </IconRowCell>
      <IconRowCell type="actions" sx={{ alignItems: 'center', display: 'flex', minHeight: '5.5rem' }}>
        <Input />
      </IconRowCell>
    </IconRow>
  );
};

export default FileUpload;
